import '../../../assets/styles/LoadingAnimation.css'
import '../../../assets/styles/Start.css'

import React, { useCallback, useEffect, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useSearchParams } from 'react-router-dom'
import { OutOfMinutesModal } from '../../../components/Modals/OutOfMinutesModal'

import { useArtifacts } from '../../../hooks/storeHooks/useArtifacts'
import { useMentorsState } from '../../../hooks/storeHooks/useMentors'
import {
  useAudioVideo,
  useCompositeData,
  useSession,
} from '../../../hooks/storeHooks/useSessionState'
import { analytics } from '../../../utils/analytics'
import { avatarIdleVideos } from '../../../utils/utils'
import { StartButton } from './StartButton'

interface StartProps {
  mentorId?: string
  isLoading: boolean
  freeTimeLeft: number
  outOfCreditsShown: boolean
  isGenerating: boolean
  isAppOpened: boolean
  setAppOpened: (isOpened: boolean) => void
}

export const Start: React.FC<StartProps> = ({
  mentorId: id,
  isLoading,
  freeTimeLeft,
  isGenerating,
  outOfCreditsShown,
  isAppOpened,
  setAppOpened,
}) => {
  const idleVideo = avatarIdleVideos[id || ''] || null
  const {
    isAuthenticated,
    signinRedirect,
    isLoading: isAuthLoading,
  } = useAuth()
  const {
    secondsBalance,
    avatarReady,
    avatarId,
    personaData,
    setAvatarId,
    setPersonaData,
    setAvatarReady,
    resetTranscript,
    isSubsDataLoading,
  } = useSession()

  const { setWebRTCStatus, webRTCStatus } = useAudioVideo()

  const { setArtifactsList, setSuggestedMentors } = useArtifacts()
  const { setCompositeData } = useCompositeData()

  const [searchParams, setSearchParams] = useSearchParams()
  const [isVisible, setIsVisible] = useState<boolean>(true)
  const startParam = searchParams.get('start')
  const contextParam = searchParams.get('context')
  const freeParam = searchParams.get('free')

  const [wasFreeParameter] = useState(freeParam === 'true')

  const { selectedMentor, setSelectedMentor } = useMentorsState()

  const getReturnUrl = () => {
    return `${location.pathname}?start=true${
      contextParam ? `&context=${contextParam}` : ''
    }`
  }

  useEffect(() => {
    if (
      !isAuthLoading &&
      !isAuthenticated &&
      personaData &&
      freeParam !== 'true' &&
      !wasFreeParameter
    ) {
      void handleSignIn()
    }
  }, [isAuthLoading, isAuthenticated, personaData, freeParam])

  const startExperience = useCallback(() => {
    if (personaData) {
      if (isAppOpened) {
        setAppOpened(false)
      }
      setAvatarId(personaData.avatar_id || '')

      if (startParam !== 'true') {
        const params: {
          context?: string
          start?: string
          free?: string
        } = { start: 'true' }

        if (contextParam) {
          params.context = contextParam
        }
        if (freeParam === 'true') {
          params.free = 'true'
        }

        setSearchParams(params)
      }
    }
  }, [
    secondsBalance,
    personaData,
    startParam,
    contextParam,
    freeParam,
    isAuthenticated,
    isLoading,
    freeTimeLeft,
    isSubsDataLoading,
  ])

  const handleSignIn = async () => {
    await signinRedirect({
      state: {
        returnUrl: getReturnUrl(),
      },
      extraQueryParams: {
        action: 'get-started',
        backgroundUrl: personaData?.videoLoopUrl || '',
      },
    })
  }

  useEffect(() => {
    if (avatarReady) {
      const timer = setTimeout(() => setIsVisible(false), 500)
      return () => clearTimeout(timer)
    }
    setIsVisible(true)
  }, [avatarReady])

  useEffect(() => {
    if (
      (secondsBalance > 0 || freeTimeLeft > 0) &&
      !avatarId &&
      startParam === 'true' &&
      personaData !== null &&
      !isLoading
    ) {
      startExperience()
    }
  }, [
    secondsBalance,
    searchParams,
    personaData,
    isLoading,
    freeTimeLeft,
    startParam,
    avatarId,
  ])

  useEffect(() => {
    if (avatarReady && !personaData?.slug) {
      setAvatarReady(false)
      setAvatarId('')
      setSuggestedMentors(null)
      resetTranscript('')
      setSelectedMentor(null)
    }
  }, [personaData?.slug, avatarReady])

  useEffect(() => {
    const event_properties = {
      page_name: contextParam ? 'watch-session' : 'regular-session',
    }
    analytics.logEvent('page_viewed', event_properties)
  }, [contextParam])

  useEffect(
    () =>
      // Cleanup function
      () => {
        setAvatarId('')
        setAvatarReady(false)
        setPersonaData(null)
        setWebRTCStatus({
          blocked: false,
          reason: '',
          message: '',
        })
        resetTranscript('')
        setArtifactsList([])
        setSuggestedMentors(null)
        setCompositeData(null)
      },
    [],
  )

  if (!isVisible || isAuthLoading || isLoading || isSubsDataLoading) return null

  return (
    <div
      data-testid="start-experience-container"
      className={`start-experience-container ${avatarReady ? 'fade-out' : ''}`}
    >
      <div className="absolute top-0 left-0 w-full h-full z-[2]">
        {!webRTCStatus.blocked && (
          <video
            loop
            autoPlay
            playsInline
            muted
            disablePictureInPicture
            src={
              idleVideo ||
              selectedMentor?.videoLoopUrl ||
              personaData?.avatar?.videos[0]?.original_video_url ||
              personaData?.videoIdleLoopUrl
            }
            poster={personaData?.avatar?.videos[0]?.preview_url || ''}
            className="w-full h-full object-cover z-[1]"
          />
        )}
      </div>
      {!startParam && personaData && (!outOfCreditsShown || isAppOpened) && (
        <>
          {isAuthenticated || (!isAuthenticated && freeParam === 'true') ? (
            <div
              className={`start-experience-content ${startParam ? 'fade-out' : ''}`}
            >
              <div className="absolute -top-[28px] right-0 flex w-full items-center justify-center flex-col">
                <StartButton
                  onClick={startExperience}
                  showByBtn={isAuthenticated && secondsBalance <= 0}
                />
              </div>
              <div className="lg:flex overflow-auto h-full">
                <div className="start-experience-content-left">
                  <div className="start-experience-title capitalize">
                    {`${personaData.firstName} ${personaData.lastName}`}
                  </div>
                  <div className="start-experience-subtitle max-w-[500px]">
                    {personaData.call_to_action || personaData.headline}
                  </div>
                </div>
                <p className="start-experience-description start-experience-content-right">
                  {personaData.summary}
                </p>
              </div>
            </div>
          ) : (
            <OutOfMinutesModal onClose={() => {}} isSessionPage />
          )}
        </>
      )}
      {!avatarReady &&
        !isAppOpened &&
        (avatarId || isGenerating) &&
        !webRTCStatus?.blocked &&
        ((secondsBalance > 0 && !isSubsDataLoading) ||
          (!isAuthenticated && freeParam === 'true')) && (
          <div className="loading-container">
            <div className="start-experience-content">
              <div className="absolute -top-[28px] right-0 flex w-full items-center justify-center">
                <div className="start-experience-btn loader">
                  <span className="button-content relative">
                    <span className="star-icon star-first">✦</span>
                    <span className="star-icon star-second large">✦</span>
                    <span className="star-icon star-third">✦</span>
                    <span className="text ms-2">
                      {isGenerating ? (
                        <span data-testid="generating-label">
                          Generating...
                        </span>
                      ) : (
                        <span data-testid="connecting-label">
                          Connecting...
                        </span>
                      )}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  )
}
